import { useCallback, useMemo } from 'react';
import { SxProps } from '@mui/material';
import dayjs from 'dayjs';
import useSearchParams from '../../../hooks/useSearchParams';
import useUrlFilters from '../../../hooks/useUrlFilters';
import { distinctArrayBy, getValue } from '../../../utils/common';
import FilterPopover from '../../FilterPopover';
import { Flex, FlexColumn } from '../../Flex';
import SearchField from '../../SearchField';
import DaysBackPicker from '../../DaysBackPicker';
import { FilterOption } from '../../FilterSelector';
import { Column, ColumnType, QuickFilter } from '../types';
import FilterChipRow from './FilterChipRow';
import QuickFilters from './QuickFilters';

type Props<T> = {
  sx?: SxProps;
  data: T[];
  columns: Column<T>[];
  urlParamsPrefix: string;
  search: string;
  onSearch: (val: string) => void;
  quickFilters?: QuickFilter[];
  clearFiltersChipLabel?: string;
  isClearFiltersLoading?: boolean;
  showDaysBackPicker?: boolean;
  daysBackUrlParam?: string;
  daysBackRange?: {
    min: number;
    max: number;
  };
  daysBackDefault?: number;
  additionalFilters?: FilterOption[];
  disabled?: boolean;
};

const TableFilters = <T extends object>({
  sx,
  data,
  columns,
  urlParamsPrefix,
  search,
  onSearch,
  quickFilters,
  clearFiltersChipLabel,
  isClearFiltersLoading = false,
  showDaysBackPicker = false,
  daysBackUrlParam = 'days_back',
  daysBackDefault = 7,
  daysBackRange = { min: 0, max: 30 },
  additionalFilters = [],
  disabled,
}: Props<T>) => {
  const { urlFilters, addFilter, clearFilter, clearAllFilters, updateFilter } =
    useUrlFilters({
      urlKey: urlParamsPrefix,
    });
  const [searchParams, setSearchParams] = useSearchParams();

  const daysBack = parseInt(
    searchParams.get(daysBackUrlParam) || daysBackDefault.toString(),
    10,
  );
  const selectedDate = dayjs().subtract(daysBack, 'day');

  const handleDaysBackChange = (date?: dayjs.Dayjs) => {
    if (date) {
      const newDaysBack = dayjs().diff(date, 'days');
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set(daysBackUrlParam, newDaysBack.toString());
        return newParams;
      });
    }
  };

  const filterableColumns = useMemo(
    () =>
      columns.filter(
        (c) =>
          (c.filterable || c.filterable === undefined) &&
          !c.hideColumn &&
          !c.hideFromTableFilters,
      ),
    [columns],
  );

  const getUnselectedValueOptions = useCallback(
    (columnId: string) => {
      const column = filterableColumns.find((c) => c.id === columnId);

      const values = column?.filterOptionsOverride
        ? column?.filterOptionsOverride
        : data.map((item: any) => ({
            value: getValue(item, columnId),
            label: getValue(item, columnId),
          }));

      const isArrayItems = values.some((item) => Array.isArray(item));
      const formattedData = isArrayItems ? values.flat() : values;

      // treat "null", "undefined" or empty string same way
      const truthyData = formattedData.map((i) => ({
        ...i,
        value: i.value ?? '',
      }));

      return distinctArrayBy(truthyData, 'value').map((i) => ({
        label: i?.label?.toString() || column?.emptyValueLabel || '',
        value: i?.value?.toString().toLowerCase() || '',
      }));
    },
    [data, filterableColumns],
  );

  const filterOptions = useMemo(() => {
    const filterOptions = filterableColumns
      .filter((c) => c.type !== ColumnType.Date)
      .map((c) => ({
        property: c.id,
        propertyLabel: c.header,
        operators: c.filterOperators,
        options: getUnselectedValueOptions(c.id),
      }));
    return [...filterOptions, ...additionalFilters];
  }, [filterableColumns, getUnselectedValueOptions, additionalFilters]);

  return (
    <Flex sx={sx}>
      <FlexColumn sx={{ gap: 2 }}>
        <Flex sx={{ gap: 1, flexWrap: 'wrap' }}>
          <SearchField
            sx={{ width: '200px' }}
            onChange={(e) => onSearch(e.target.value)}
            value={search}
          />

          {showDaysBackPicker && (
            <DaysBackPicker
              sx={{ width: '136px' }}
              value={selectedDate}
              onChange={handleDaysBackChange}
              menuHorizontalAnchor="right"
              minDate={dayjs().subtract(daysBackRange.max, 'day')} // max daysBack value is the earliest valid date (min date)
              maxDate={dayjs().subtract(daysBackRange.min, 'day')}
              disabled={disabled}
            />
          )}

          <FilterPopover
            filterOptions={filterOptions}
            onSubmit={(val) => {
              const newValue = (val.value || val.prevValue).filter(
                (v) => v !== 'all',
              );

              if (val.prevValue?.length || val.prevValue?.includes('all')) {
                updateFilter(
                  val.property,
                  val.operator,
                  val.prevValue.filter((v) => v !== 'all'),
                  newValue,
                );
              } else {
                addFilter(val.property, val.operator, newValue);
              }
            }}
            multiselect
            disabled={disabled}
          />

          {quickFilters && (
            <QuickFilters
              urlParamsPrefix={urlParamsPrefix}
              quickFilters={quickFilters}
              clearFiltersChipLabel={clearFiltersChipLabel}
              disabled={disabled}
              isClearFiltersLoading={isClearFiltersLoading}
            />
          )}
        </Flex>

        {urlFilters && (
          <FilterChipRow
            filterOptions={filterOptions}
            onSubmit={(val) =>
              updateFilter(
                val.property,
                val.operator,
                val.prevValue!,
                val.value,
                val.index,
              )
            }
            onAddFilter={(val) => {
              const newValue = (val.value || val.prevValue).filter(
                (v) => v !== 'all',
              );
              addFilter(val.property, val.operator, newValue);
            }}
            selectedFilters={urlFilters}
            clearFilter={clearFilter}
            onClearAll={clearAllFilters}
            getLabel={(property) =>
              columns.find((c) => c.id === property)?.header
            }
            getEmptyValueLabel={(property) =>
              columns.find((c) => c.id === property)?.emptyValueLabel || 'N/A'
            }
            getType={(property) =>
              columns.find((c) => c.id === property)?.type || ColumnType.Text
            }
          />
        )}
      </FlexColumn>
    </Flex>
  );
};

export default TableFilters;
